import { Check, X } from "lucide-react";
import type { OrderStage } from "./types";
import clsx from "clsx";

interface ProgressBarProps {
    stages: OrderStage[];
}

export function ProgressBar({ stages }: ProgressBarProps) {
    const lastCompletedIndex = stages.reduce(
        (acc, stage, index) => (stage.isCompleted ? index : acc),
        -1,
    );

    // Four dots positions
    const milestones = stages.length === 4 ? [0, 33.33, 66.66, 100] : [0, 25, 50, 75, 100];

    // Get the base progress calculation
    const getBaseProgress = () => {
        if (lastCompletedIndex === -1) return 0;

        const nextStage = stages[lastCompletedIndex + 1];
        const isPartialStage = nextStage?.date && nextStage?.value !== "Order Rejected";

        if (isPartialStage) {
            return Math.min(((lastCompletedIndex + 2) / (stages.length - 1)) * 100, 100);
        }

        if (nextStage?.value === "Order Rejected") {
            return Math.min(((lastCompletedIndex + 1) / (stages.length - 1)) * 100, 100);
        }

        return Math.min(((lastCompletedIndex + 1) / (stages.length - 1)) * 100, 100);
    };

    const progress = getBaseProgress();

    const getSectionColors = () => {

        const sections = stages.length === 4 ? [
            {
                start: 0,
                end: 33.33,
                color: stages[0].value === "Order Failed" ? "bg-error-500" : "bg-success-700",
            },
            {
                start: 33.33,
                end: 66.66,
                color: stages[1].value === "Payment Failed" ? "bg-neutral-50" :  stages[1].isCompleted && stages[1].date ? "bg-success-700" : "bg-amber-500",
            },
            {
                start: 66.66,
                end: 100,
                color:
                    stages[2].label === "Order Rejected"
                        ? "bg-error-500"
                        : stages[2].label === "Order Processed" &&
                            stages[2]?.status === "inProgress"
                          ? "bg-amber-500"
                          : "bg-success-700",
            },
        ] : [ {
                start: 0,
                end: 25,
                color: stages[0].value === "Order Failed" ? "bg-error-500" : "bg-success-700",
            },
            {
                start: 25,
                end: 50,
                color: stages[1].value === "Order Cancelled" ? "bg-error-500" : "",
            },
            {
                start: 50,
                end: 75,
                color: stages[1].value === "Payment Failed" ? "bg-neutral-50" :  stages[1].isCompleted && stages[1].date ? "bg-success-700" : "bg-amber-500",
            },
            {
                start: 75,
                end: 100,
                color:
                    stages[2].label === "Order Rejected"
                        ? "bg-error-500"
                        : stages[2].label === "Order Processed" &&
                            stages[2]?.status === "inProgress"
                          ? "bg-amber-500"
                          : "bg-success-700",
            }];

        return sections;
    };

    const getMilestoneClass = (stage: OrderStage, milestone: number) => {
        console.log("Stage:", stage, "Milestone:", milestone);
        
        return clsx(
            "absolute top-1/2 -ml-2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border-[1px]",
            {
                "bg-amber-500": stage.isCompleted && !stage.date,
                "bg-success-700":
                    stage.isCompleted &&
                    stage.value !== "Order Failed" &&
                    stage?.status !== "inProgress" &&
                    stage.date,
                "bg-error-500": stage.value === "Order Rejected" || stage.value === "Order Cancelled" || stage.value === "Order Failed" || stage.value === "Payment Failed",
                "bg-white":
                    !stage.isCompleted &&
                    stage.value !== "Order Failed" &&
                    stage.value !== "Payment Failed" &&
                    stage.value !== "Order Rejected" && stage.value !== "Order Cancelled",
                "right-0": milestone === 100,
            },
        );
    };

    const setStageColors = (stage: OrderStage) => {
        if (stage.value === "Order Failed" || stage.value === "Order Rejected" || stage.value === "Order Cancelled" || stage.value === "Payment Failed") {
            return "text-red-500";
        }
        if (stage.value === "Order Created") return "text-success-700";
        if (stage.value === "Order Processed") {
            return stage.isCompleted && stage.date
                ? "text-success-700"
                : stage.status === "inProgress"
                  ? "text-amber-500"
                  : "text-neutral-300";
        }
        if (stage.value === "Payment Received") {
            return stage.isCompleted && stage.date ? "text-success-700" : !stage.isCompleted ? "text-neutral-300" : "text-amber-500";
        }
        if (stage.value === "Order Settled" && stage.isCompleted) {
            return "text-success-700";
        } else {
            return "text-neutral-300";
        }
    };

    return (
        <div className="relative">
            <div className="relative h-2 w-[90%] rounded bg-gray-100">
                {getSectionColors().map((section, index) => {
                    const sectionProgress = Math.max(
                        0,
                        Math.min(progress - section.start, section.end - section.start),
                    );
                    return (
                        <div
                            key={index}
                            className={`absolute h-full transition-all duration-300 ${section.color}`}
                            style={{
                                width: `${sectionProgress}%`,
                                left: `${section.start}%`,
                                borderRadius:
                                    index === 0
                                        ? "0.25rem 0 0 0.25rem"
                                        : index === getSectionColors().length - 1
                                          ? "0 0.25rem 0.25rem 0"
                                          : "none",
                            }}
                        />
                    );
                })}
                <div className="absolute inset-0">
                    {milestones.map((milestone, index) => (
                        <div
                            key={milestone}
                            className={getMilestoneClass(stages[index], milestone)}
                            style={milestone !== 100 ? { left: `${milestone}%` } : {}}
                        >
                            {stages[index].isCompleted &&
                            stages[index].value !== "Order Failed" &&
                            stages[index]?.status !== "inProgress" &&
                            stages[index].date ? (
                                <Check size={15} className="text-white" />
                            ) : null}
                            {stages[index].value === "Order Failed" || stages[index].value === "Order Cancelled" ||
                            stages[index].value === "Order Rejected" || stages[index].value === "Payment Failed" ? (
                                <X
                                    size={15}
                                    className={
                                        !stages[index].isCompleted
                                            ? stages[index].value === "Order Failed" ||
                                              stages[index].value === "Order Cancelled" ||
                                              stages[index].value === "Order Rejected" || stages[index].value === "Payment Failed"
                                                ? "text-white"
                                                : "text-black"
                                            : "text-white"
                                    }
                                />
                            ) : null}
                        </div>
                    ))}
                </div>
            </div>
            <div className="my-3 flex justify-between text-sm">
                {stages.map((stage, index) => (
                    <div
                        key={index}
                        className={`${index === stages.length - 1 ? "text-center" : ""}`}
                    >
                        <div className={`text-sm font-regular ${setStageColors(stage)}`}>
                            {stage.value}
                        </div>
                        <div className="text-neutral-300">{`${stage.label === "Order Settled" ? "Estimated: " : ""} ${stage.date || "--"}`}</div>
                        {stage.isCompleted && stage.label === "Order Settled" ? (
                            <div className="text-neutral-300">
                                {stage.actualSettlementDate || "--"}
                            </div>
                        ) : null}
                    </div>
                ))}
            </div>
        </div>
    );
}
